import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { isMobileDevice, getViewportHeight, scrollIntoViewWithKeyboard } from '../../utils/mobileUtils';
import { mobileFocusManager } from '../../utils/MobileFocusManager';

const PlainTextEditor = forwardRef(({ 
  value = '', 
  onChange, 
  placeholder = 'Enter your content...', 
  maxLength = 5000,
  className = '',
  error = false,
  disabled = false,
  onBlur,
  onKeyUp,
  autoFocus = false,
  isMobile = false,
  rows = 6
}, ref) => {
  const textareaRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  // Expose ref to parent
  useImperativeHandle(ref, () => textareaRef.current);

  // Handle content changes
  const handleContentChange = useCallback((e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  }, [onChange]);

  // Handle key down to prevent line breaks
  const handleKeyDown = useCallback((e) => {
    // Prevent Enter key from creating line breaks
    if (e.key === 'Enter') {
      e.preventDefault();
      return;
    }
  }, []);

  // Enhanced mobile focus handling
  const handleFocus = useCallback((e) => {
    setIsFocused(true);
    
    if (isMobile) {
      // Set keyboard visible state
      setKeyboardVisible(true);
      
      // Ensure proper mobile focus
      const element = e.target;
      setTimeout(() => {
        if (element && element.focus) {
          element.focus();
          
          // Position cursor at end for mobile
          try {
            const length = element.value.length;
            element.setSelectionRange(length, length);
          } catch (error) {
            console.debug('Failed to position cursor:', error);
          }
        }
      }, 50);
      
      // Scroll into view with keyboard compensation
      scrollIntoViewWithKeyboard(element, 300);
    }
  }, [isMobile]);

  const handleBlur = useCallback((e) => {
    setIsFocused(false);
    setKeyboardVisible(false);
    
    if (onBlur) {
      onBlur(e);
    }
  }, [onBlur]);

  // Auto-focus for mobile with browser-specific handling
  useEffect(() => {
    if (autoFocus && textareaRef.current && isMobile) {
      // Delay focus to ensure DOM is ready
      const timer = setTimeout(async () => {
        if (textareaRef.current) {
          try {
            await mobileFocusManager.focusWithKeyboard(textareaRef.current);
          } catch (error) {
            console.debug('Auto-focus failed:', error);
            
            // Fallback for browsers that don't support the mobile focus manager
            textareaRef.current.focus();
            
            // Additional fallback for iOS Safari
            if (navigator.userAgent.includes('Safari') && navigator.userAgent.includes('iPhone')) {
              setTimeout(() => {
                if (textareaRef.current) {
                  textareaRef.current.click();
                  textareaRef.current.focus();
                }
              }, 50);
            }
          }
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [autoFocus, isMobile]);

  // Handle mobile keyboard visibility changes with browser compatibility
  useEffect(() => {
    if (!isMobile) return;
    
    const handleResize = () => {
      const currentHeight = getViewportHeight();
      const screenHeight = window.screen.height;
      
      // Detect keyboard visibility based on viewport height change
      // Different thresholds for different browsers
      let keyboardThreshold = screenHeight * 0.75;
      
      // iOS Safari has different behavior
      if (navigator.userAgent.includes('Safari') && navigator.userAgent.includes('iPhone')) {
        keyboardThreshold = screenHeight * 0.6;
      }
      
      // Android Chrome
      if (navigator.userAgent.includes('Chrome') && navigator.userAgent.includes('Android')) {
        keyboardThreshold = screenHeight * 0.7;
      }
      
      const isKeyboardVisible = currentHeight < keyboardThreshold;
      setKeyboardVisible(isKeyboardVisible);
    };
    
    // Use different event listeners based on browser support
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isMobile]);

  // Get current text length
  const getCurrentLength = () => {
    return value?.length || 0;
  };

  return (
    <div 
      className={`border rounded-md ${
        isFocused ? 'border-purple-500 ring-1 ring-purple-500' : 
        error ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
      } ${className}`}
    >
      {/* Plain textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleContentChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onKeyUp={onKeyUp}
        onTouchStart={(e) => {
          // Prevent double-tap zoom and improve touch responsiveness
          if (isMobile) {
            e.stopPropagation();
          }
        }}
        onClick={(e) => {
          // Always focus immediately on click
          if (!disabled) {
            e.currentTarget.focus();
            
            // Additional handling for mobile browsers to ensure proper focus
            if (isMobile && !isFocused) {
              // Use requestAnimationFrame for better timing
              requestAnimationFrame(() => {
                if (e.currentTarget) {
                  e.currentTarget.focus();
                  
                  // For iOS Safari, ensure cursor positioning
                  if (navigator.userAgent.includes('Safari') && navigator.userAgent.includes('iPhone')) {
                    setTimeout(() => {
                      if (e.currentTarget) {
                        e.currentTarget.focus();
                        // Position cursor at end
                        const length = e.currentTarget.value.length;
                        e.currentTarget.setSelectionRange(length, length);
                      }
                    }, 10);
                  }
                }
              });
            }
          }
        }}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
        disabled={disabled}
        role="textbox"
        aria-multiline="false"
        aria-label="Content editor"
        aria-describedby={isMobile ? undefined : "editor-help"}
        aria-required="false"
        className={`w-full p-3 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md resize-vertical ${
          disabled ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''
        }`}
        style={{ 
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          // Mobile-specific styles
          ...(isMobile && {
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation'
          })
        }}
      />

      {/* Character count - Hidden on mobile to save space */}
      {!isMobile && (
        <div 
          id="editor-help"
          className="flex justify-between items-center px-3 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 text-xs"
        >
          <div className="text-gray-500 dark:text-gray-400">
            Plain text only - URLs will be automatically detected. Line breaks are not allowed.
          </div>
          <div 
            className={`${
              getCurrentLength() > maxLength * 0.9 
                ? getCurrentLength() > maxLength 
                  ? 'text-red-500' 
                  : 'text-yellow-500'
                : 'text-gray-500 dark:text-gray-400'
            }`}
            aria-label={`${getCurrentLength()} of ${maxLength} characters used`}
          >
            {getCurrentLength()}/{maxLength}
          </div>
        </div>
      )}
      
      {/* Screen reader only character count for mobile */}
      {isMobile && (
        <div className="sr-only" aria-live="polite">
          {getCurrentLength() > maxLength * 0.9 && (
            `${getCurrentLength()} of ${maxLength} characters used${
              getCurrentLength() > maxLength ? '. Character limit exceeded.' : ''
            }`
          )}
        </div>
      )}
    </div>
  );
});

PlainTextEditor.displayName = 'PlainTextEditor';

export default PlainTextEditor;